'use client';

import { useFeatureFlag } from 'configcat-react';
import { usePathname, useRouter } from 'next/navigation';
import { signIn, signOut, useSession } from 'next-auth/react';
import { ComponentProps, useMemo } from 'react';

import { AppHeader as AppHeaderComponent } from '@repo/ui/components/app-header';

import { bmAdAstraLogo } from '~/assets';
import { FeatureFlag } from '~/definitions/feature-flag';
import { HEADER_LABELS } from '~/definitions/labels';
import {
  CONTENT_LIBRARY_PAGE_ROUTE,
  CONTENT_LINK_CAMPAIGNS_PAGE_ROUTE,
  HOME_PAGE_ROUTE,
  ROUTES,
} from '~/definitions/routes';
import { handleUnsavedProgress } from '~/utils/handleUnsavedProgress';
import { clearLinkCampaignsSelection } from '~/utils/link-campaigns-selection';

import { buildNavLinksFromRoutes } from './app-header-nav-links';

type AppHeaderNavLink = NonNullable<ComponentProps<typeof AppHeaderComponent>['navLinks']>[number];

export const AppHeader = () => {
  const { push } = useRouter();
  const pathname = usePathname();
  const { data: session } = useSession();

  const { value: hasCampaignsNavigation } = useFeatureFlag(FeatureFlag.dbd477, false);
  const { value: hasContentCampaignsNavigation } = useFeatureFlag(FeatureFlag.dbd880, false);
  const { value: hasAutomatedCampaignsNavigation } = useFeatureFlag(FeatureFlag.lpad313ShowAutomatedCampaigns, false);

  const featureFlags = useMemo(
    () => ({
      [FeatureFlag.dbd477]: hasCampaignsNavigation,
      [FeatureFlag.dbd880]: hasContentCampaignsNavigation,
      [FeatureFlag.lpad313ShowAutomatedCampaigns]: hasAutomatedCampaignsNavigation,
    }),
    [hasCampaignsNavigation, hasContentCampaignsNavigation, hasAutomatedCampaignsNavigation],
  );

  const userRoles = useMemo(() => session?.user.token.roles || [], [session?.user?.token?.idToken]);

  const userInitials = useMemo(() => {
    if (!session?.user?.token?.name) return HEADER_LABELS.UNKNOWN_INITIALS;

    const names = session?.user?.token.name.split(/[,\s]+/).filter(Boolean);
    const fistName = names.pop();
    const MAX_INITIALS = 3;
    const initials = ([fistName, ...names] as string[])
      .slice(0, MAX_INITIALS)
      .map((part) => part[0])
      .join('')
      .toUpperCase();

    return initials;
  }, [session?.user?.token?.name]);

  const onNavbarClick = (page: string) => {
    if (pathname.includes('content-moderation')) {
      if (!handleUnsavedProgress()) {
        return;
      }
    }
    // Leaving the content-linking flow (Content Library / Link Campaigns) via the navbar discards the
    // in-flight selection; the in-page Cancel/Back and the dashboard "Link campaigns" button manage it
    // themselves. sessionStorage otherwise survives a refresh and is dropped on tab close.
    if (pathname.includes(CONTENT_LINK_CAMPAIGNS_PAGE_ROUTE) || pathname.includes(CONTENT_LIBRARY_PAGE_ROUTE)) {
      clearLinkCampaignsSelection();
    }
    push(page);
  };

  const avatarMenu = session
    ? {
        avatar: {
          label: userInitials,
          tooltip: HEADER_LABELS.AVATAR_TOOLTIP,
        },
        menuItems: [
          {
            label: HEADER_LABELS.SIGN_OUT,
            onClick: async () => {
              await signOut({ redirect: false });
              window.location.href = '/api/auth/upstream-signout';
            },
          },
        ],
      }
    : {
        button: {
          label: HEADER_LABELS.SIGN_IN,
          // eslint-disable-next-line camelcase
          onClick: () => signIn('cognito', undefined, { identity_provider: 'clearchannel-ad' }),
        },
      };

  const navLinks = useMemo(
    () =>
      buildNavLinksFromRoutes({
        featureFlags,
        hasSession: Boolean(session),
        onNavbarClick,
        pathname,
        routeEntries: Object.entries(ROUTES),
        userRoles,
      }) as AppHeaderNavLink[],
    [featureFlags, pathname, session, userRoles],
  );

  return (
    <AppHeaderComponent
      avatarMenu={avatarMenu}
      logo={{ ...bmAdAstraLogo, onClick: () => onNavbarClick(HOME_PAGE_ROUTE) }}
      navLinks={navLinks}
      testId="app-header"
    />
  );
};
